import { updateState, withDevtools } from '@angular-architects/ngrx-toolkit';
import { Injector, computed, inject, runInInjectionContext } from '@angular/core';
import { signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { Observable, finalize, map, of, shareReplay, tap } from 'rxjs';

import { KeyValueIsUsedModel, KeyValueType } from '@models/common';
import {
  FolderFileService,
  TemplateDefinitionRequestParams,
  TemplateDefinitionService,
} from '@module-instance/services';
import { DataSourceInfoModel } from '@shared/components/fields/common';
import { UserStore } from '@signal-store/index';
import { DictionaryService, makeKey } from '@signal-store/services/dictionary.service';
import {
  DictionaryParams,
  DictionaryStoreActions,
  DictionaryType,
  DictionaryValidTime,
  PredefinedDictionaryKey,
  initialDatasourceState as initialState,
} from './models';

/* This store should not inject any other dependecies, to keep it pure and avoid circular dependencies */
export const DictionaryStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('dictionary'),

  withComputed(({ predefined }) => ({
    /** get module roles based on makeKey(params). Ex: store.moduleRoles()[makeKey({contractId: 123, moduleId: 456})] */
    moduleRoles: computed(() => {
      return predefined().moduleRoles ?? {};
    }),
  })),

  withMethods(store => {
    const injector = inject(Injector);
    const dictionaryService = inject(DictionaryService);
    const folderFileService = inject(FolderFileService);
    const templateDefinitionService = inject(TemplateDefinitionService);

    const setDynamicDictionary = (params: Partial<DictionaryParams>, data: KeyValueType[]) => {
      const name = makeKey(params);
      const timestamp = Date.now();
      updateState(store, DictionaryStoreActions.SetScopedDictionary, ({ dynamic }) => ({
        dynamic: { ...dynamic, [name]: { data, timestamp } },
      }));
    };

    // TODO: replace with DEFAULT validity after testing
    const isValid = (dict: DictionaryType<any>, validity = DictionaryValidTime.SHORT) => {
      //   if (store.instantResponse()) {
      //     return false;
      //   }
      return dict && dict.timestamp + validity > Date.now();
    };

    const inFlightLoads = new Map<string, Observable<unknown>>();

    const shareInflightLoad = <T>(key: string, factory: () => Observable<T>): Observable<T> => {
      const existing = inFlightLoads.get(key);
      if (existing) {
        return existing as Observable<T>;
      }
      const shared = factory().pipe(
        shareReplay({ bufferSize: 1, refCount: false }),
        finalize(() => inFlightLoads.delete(key)),
      );
      inFlightLoads.set(key, shared);
      return shared;
    };

    return {
      //   toggleInstant: () =>
      //     updateState(store, DictionaryStoreActions.ToggleInstant, ({ instantResponse }) => ({
      //       instantResponse: !instantResponse,
      //     })),
      /** Used to manually set a dynamic dictionary */
      setDynamicDictionary,

      getFieldDataSource: <T = KeyValueType>(params: Partial<DataSourceInfoModel>) => {
        const key = makeKey(params);
        const dict = store.dynamic()[key] as DictionaryType<T>;
        if (isValid(dict, DictionaryValidTime.DEFAULT)) {
          return of(dict.data);
        }
        /* We need the explicit id here because it's optional in ...params, but required in .getDataSource() */
        return shareInflightLoad(key, () =>
          dictionaryService
            .getDataSource({ ...params, id: params.id })
            .pipe(tap(data => setDynamicDictionary(params, data))),
        );
      },

      getRelationDataSource: <T = KeyValueIsUsedModel<string, string>>(
        params: Pick<
          DictionaryParams,
          'contractId' | 'relationId' | 'entityInstanceId' | 'moduleId' | 'fieldId' | 'selectedIds'
        >,
      ) => {
        const key = makeKey(params);
        const dict = store.dynamic()[key] as DictionaryType<T>;
        // Using a shorter validity for relation data sources because they are updated on each save of a new document
        const activeModuleId = runInInjectionContext(injector, () => inject(UserStore).menu.activeMenuItem()?.moduleId);
        const validity = activeModuleId === params.moduleId ? DictionaryValidTime.SHORT : DictionaryValidTime.DEFAULT;
        if (isValid(dict, validity)) {
          return of(dict.data);
        }

        return shareInflightLoad(key, () =>
          dictionaryService.getSearchEntityInstances(params).pipe(tap(data => setDynamicDictionary(params, data))),
        );
      },

      /** Used to get the list of currencies. This is a static list, so it's loaded only once */
      getCurrencies: () => {
        const currencies = store.predefined().currencies;
        if (currencies) {
          return of(currencies);
        }
        return shareInflightLoad('currencies', () =>
          dictionaryService.getCurrencies().pipe(
            tap(currencies => {
              updateState(store, DictionaryStoreActions.SetCurrencies, ({ predefined }) => ({
                predefined: { ...predefined, currencies },
              }));
            }),
          ),
        );
      },

      /** Used to get organizations as key-value list, keyed by contractId */
      getOrganizations: (contractId?: number) => {
        const key = makeKey({ contractId });
        const organizations = store.predefined().organizations?.[key];
        if (isValid(organizations, DictionaryValidTime.DEFAULT)) {
          return of(organizations.data);
        }
        return shareInflightLoad(key, () =>
          dictionaryService.getOrganizations(contractId).pipe(
            tap(data => {
              updateState(store, DictionaryStoreActions.SetOrganizations, ({ predefined }) => ({
                predefined: {
                  ...predefined,
                  organizations: { ...predefined.organizations, [key]: { data, timestamp: Date.now() } },
                },
              }));
            }),
          ),
        );
      },

      /** Used to get the list of roles. This is a static per tenant, so it's loaded always with the same parameters */
      getRoles: () => {
        const roles = store.predefined().roles;
        if (isValid(roles)) {
          return of(roles.data);
        }
        return shareInflightLoad('roles', () =>
          dictionaryService.getRoles().pipe(
            tap(roles => {
              updateState(store, DictionaryStoreActions.SetRoles, ({ predefined }) => ({
                predefined: { ...predefined, roles: { data: roles, timestamp: Date.now() } },
              }));
            }),
          ),
        );
      },

      /** Used to get the list of primary users for a contract.
       * This is a static per contract, so it's loaded after time expires or contract changes */
      getPrimaryUsers: (params: Pick<DictionaryParams, 'contractId'>) => {
        const key = makeKey(params);
        const primaryUsers = store.predefined().primaryUsers?.[key];
        if (isValid(primaryUsers)) {
          return of(primaryUsers.data);
        }
        return shareInflightLoad(key, () =>
          dictionaryService.getPrimaryUsers(params.contractId).pipe(
            tap(primaryUsers => {
              updateState(store, DictionaryStoreActions.SetPrimaryUsers, ({ predefined }) => ({
                predefined: {
                  ...predefined,
                  primaryUsers: { ...predefined.primaryUsers, [key]: { data: primaryUsers, timestamp: Date.now() } },
                },
              }));
            }),
          ),
        );
      },

      /** Used to get the list of module roles for a module.
       * This will be useful after implementing signalR. Now we have the implementation in document details store
       */
      getModuleRoles: (params: Pick<DictionaryParams, 'moduleId' | 'contractId'>) => {
        const key = makeKey(params);
        const moduleRoles = store.predefined().moduleRoles?.[key];
        if (isValid(moduleRoles)) {
          return of(moduleRoles.data);
        }
        return shareInflightLoad(key, () =>
          dictionaryService.getModuleRoles(params).pipe(
            tap(moduleRoles => {
              updateState(store, DictionaryStoreActions.SetModuleRoles, ({ predefined }) => ({
                predefined: {
                  ...predefined,
                  moduleRoles: { ...predefined.moduleRoles, [key]: { data: moduleRoles, timestamp: Date.now() } },
                },
              }));
            }),
          ),
        );
      },

      /** Used to get the list of module user roles for a module.
       * This will be useful after implementing signalR. Now we have the implementation in document details store
       */
      getModuleUserRoles: (params: Pick<DictionaryParams, 'moduleId' | 'contractId'>) => {
        const key = makeKey(params);
        const moduleUserRoles = store.predefined().moduleUserRoles?.[key];
        if (isValid(moduleUserRoles)) {
          return of(moduleUserRoles.data);
        }
        return shareInflightLoad(key, () =>
          dictionaryService.getModuleUserRoles(params).pipe(
            tap(moduleUserRoles => {
              updateState(store, DictionaryStoreActions.SetModuleUserRoles, ({ predefined }) => ({
                predefined: {
                  ...predefined,
                  moduleUserRoles: {
                    ...predefined.moduleUserRoles,
                    [key]: { data: moduleUserRoles, timestamp: Date.now() },
                  },
                },
              }));
            }),
          ),
        );
      },

      /** Get folder files (my drive and project files) */
      getFolderFiles: (
        id: number,
        entityInstanceId: number,
        isFolders = false,
        rootModuleId?: number,
        isGlobal = false,
        /** Skips the cached entry and refills it — used to refresh a single folder after a change. */
        forceReload = false,
      ) => {
        const key = makeKey({ dataSourceId: id, isFolders, entityInstanceId, rootModuleId, isGlobal });
        const folderFiles = store.predefined().folderFiles?.[key];
        if (!forceReload && isValid(folderFiles)) {
          return of(folderFiles.data);
        }

        return shareInflightLoad(key, () =>
          folderFileService.search({ id, isFolders, entityInstanceId, rootModuleId, isGlobal }).pipe(
            map(({ data }) => data),
            tap(data => {
              updateState(store, DictionaryStoreActions.SetFolderFiles, ({ predefined }) => ({
                predefined: {
                  ...predefined,
                  folderFiles: { ...predefined.folderFiles, [key]: { data, timestamp: Date.now() } },
                },
              }));
            }),
          ),
        );
      },

      clearFolderFiles: () =>
        updateState(store, DictionaryStoreActions.SetFolderFiles, ({ predefined }) => ({
          predefined: { ...predefined, folderFiles: null },
        })),

      getUserTemplates: (params: TemplateDefinitionRequestParams) => {
        const key = makeKey(params);
        const userTemplates = store.predefined().userTemplates?.[key];
        if (isValid(userTemplates)) {
          return of(userTemplates.data);
        }
        return shareInflightLoad(key, () =>
          templateDefinitionService.getUserTemplatesDict(params).pipe(
            tap(userTemplates => {
              updateState(store, DictionaryStoreActions.SetUserTemplates, ({ predefined }) => ({
                predefined: {
                  ...predefined,
                  userTemplates: { ...predefined.userTemplates, [key]: { data: userTemplates, timestamp: Date.now() } },
                },
              }));
            }),
          ),
        );
      },

      getTagUsers: (params: Pick<DictionaryParams, 'contractId' | 'moduleId'>) => {
        const key = makeKey(params);
        const tagUsers = store.predefined().tagUsers?.[key];
        if (isValid(tagUsers, DictionaryValidTime.DEFAULT)) {
          return of(tagUsers.data);
        }
        return shareInflightLoad(key, () =>
          dictionaryService.taggingUsersDict(params).pipe(
            tap(tagUsers => {
              updateState(store, DictionaryStoreActions.SetTagUsers, ({ predefined }) => ({
                predefined: {
                  ...predefined,
                  tagUsers: { ...predefined.tagUsers, [key]: { data: tagUsers, timestamp: Date.now() } },
                },
              }));
            }),
          ),
        );
      },

      clearState: () => {
        inFlightLoads.clear();
        updateState(store, DictionaryStoreActions.ClearState, initialState);
      },
      clearList: (listName: PredefinedDictionaryKey) => {
        if (listName === 'dynamic' || listName === 'roles') {
          inFlightLoads.clear();
        }
        switch (listName) {
          case 'dynamic':
            updateState(store, DictionaryStoreActions.ClearState, { dynamic: {} });
            break;
          case 'roles':
            updateState(store, DictionaryStoreActions.ClearState, ({ predefined }) => ({
              predefined: {
                ...predefined,
                roles: null,
                moduleRoles: null,
                moduleUserRoles: null,
              },
              dynamic: {}, // clearing dynamic as well because roles are used in some dynamic dictionaries
            }));
            break;
          default:
            updateState(store, DictionaryStoreActions.ClearState, ({ predefined }) => ({
              predefined: { ...predefined, [listName]: null },
            }));
        }
      },

      /** Used to get the list of all tenant users for dropdowns (e.g. manager selection). Cached with SHORT validity. */
      getUsersDict: () => {
        const usersDict = store.predefined().usersDict;
        if (isValid(usersDict)) {
          return of(usersDict.data);
        }
        return shareInflightLoad('usersDict', () =>
          dictionaryService.getUsersDict().pipe(
            tap(data => {
              updateState(store, DictionaryStoreActions.SetUsersDict, ({ predefined }) => ({
                predefined: { ...predefined, usersDict: { data, timestamp: Date.now() } },
              }));
            }),
          ),
        );
      },

      getMimeTypesDict: () => {
        const mimeTypesDict = store.predefined().mimeTypesDict;
        if (isValid(mimeTypesDict, DictionaryValidTime.DEFAULT)) {
          return of(mimeTypesDict.data);
        }
        return shareInflightLoad('mimeTypesDict', () =>
          dictionaryService.getMimeTypesDict().pipe(
            tap(data => {
              updateState(store, DictionaryStoreActions.SetMimeTypesDict, ({ predefined }) => ({
                predefined: { ...predefined, mimeTypesDict: { data, timestamp: Date.now() } },
              }));
            }),
          ),
        );
      },

      /** Used to get the list of contracts for a user. Keyed by userId (0 = all contracts for new user). */
      getContractsDict: (userId = 0) => {
        const key = makeKey({ userId });
        const contractsDict = store.predefined().contractsDict?.[key];
        if (isValid(contractsDict)) {
          return of(contractsDict.data);
        }
        return shareInflightLoad(key, () =>
          dictionaryService.getContractsDict(userId).pipe(
            tap(data => {
              updateState(store, DictionaryStoreActions.SetContractsDict, ({ predefined }) => ({
                predefined: {
                  ...predefined,
                  contractsDict: { ...predefined.contractsDict, [key]: { data, timestamp: Date.now() } },
                },
              }));
            }),
          ),
        );
      },

      /** Used to get the list of country codes. Static data, loaded only once */
      getCountryCodes: () => {
        const countryCodes = store.predefined().countryCodes;
        if (countryCodes) {
          return of(countryCodes.data);
        }
        return shareInflightLoad('countryCodes', () =>
          dictionaryService.getCountryCodes().pipe(
            tap(data => {
              updateState(store, DictionaryStoreActions.SetCountryCodes, ({ predefined }) => ({
                predefined: { ...predefined, countryCodes: { data, timestamp: Date.now() } },
              }));
            }),
          ),
        );
      },

      /** Used to get the list of industries. Static data, loaded only once */
      getIndustries: () => {
        const industries = store.predefined().industries;
        if (industries) {
          return of(industries.data);
        }
        return shareInflightLoad('industries', () =>
          dictionaryService.getIndustries().pipe(
            tap(data => {
              updateState(store, DictionaryStoreActions.SetIndustries, ({ predefined }) => ({
                predefined: { ...predefined, industries: { data, timestamp: Date.now() } },
              }));
            }),
          ),
        );
      },

      /** Used to get the list of financial schedule interval types. Static data, loaded only once */
      getIntervalDict: () => {
        const intervalDict = store.predefined().intervalDict;
        if (isValid(intervalDict)) {
          return of(intervalDict.data);
        }
        return shareInflightLoad('intervalDict', () =>
          dictionaryService.getIntervalDict().pipe(
            tap(data => {
              updateState(store, DictionaryStoreActions.SetIntervalDict, ({ predefined }) => ({
                predefined: { ...predefined, intervalDict: { data, timestamp: Date.now() } },
              }));
            }),
          ),
        );
      },
    };
  }),
);
